import {Text} from '@chakra-ui/react';
import {TypographyProps} from '@chakra-ui/system';
import {Trans, useTranslation} from 'react-i18next';
import {useHalState} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {PanierDesc} from 'api-types/ffconnect-hal';
import {toDecimalFr} from 'ff-common';
import {usePeutVoirMarges} from '@/hooks/auth/usePermissionsCommerciales';

interface MargesCommandeProps {
  panierNav: Navigable<PanierDesc> | undefined;
  // Style du texte (par défaut : sm / gray.dark, aligné comme le récap).
  color?: string;
  fontSize?: TypographyProps['fontSize'];
  textAlign?: TypographyProps['textAlign'];
}

/**
 * Affiche la marge agrégée du panier entier (VM / TM / MK) — uniquement en mode commercial.
 *
 * Rendue sous le bouton « Finaliser la commande » du side panel et sous le Total TTC du
 * récapitulatif de la page panier.
 *
 */
export const MargesCommande: React.FC<MargesCommandeProps> = ({
  panierNav,
  color = 'gray.dark',
  fontSize = 'sm',
  textAlign,
}) => {
  const {t} = useTranslation();
  // Permission `voir-les-marges` (absente en mode client et assistance)
  const peutVoirMarges = usePeutVoirMarges();
  const margesState = useHalState(
    peutVoirMarges && panierNav ? panierNav.follow('marges_panier') : undefined,
  );
  const marges = margesState.data;

  if (!peutVoirMarges || marges?.valeur_marge_totale === undefined) {
    return null;
  }

  return (
    <Text
      fontSize={fontSize}
      color={color}
      fontWeight='400'
      textAlign={textAlign}
    >
      <Trans
        t={t}
        i18nKey='back_office.prise_de_commande.panier.marges_totales'
        values={{
          vm: toDecimalFr(marges.valeur_marge_totale, 2),
          tm: toDecimalFr(marges.taux_marge_moyen ?? 0, 0),
          mk: toDecimalFr(marges.marge_kg_totale ?? 0, 2),
        }}
        components={{
          bold: (
            <Text
              as='span'
              fontWeight='700'
            />
          ),
        }}
      />
    </Text>
  );
};
